import type { DaySheet } from "@kitchen/schemas";
import { Button, faDigits, formatDate, formatMoney, formatTime, Icon, Panel } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { eventsApi } from "../../api.js";
import { ErrorLine } from "../common.js";

const fmtDate = (iso: string) => formatDate(iso.slice(0, 10));

interface DaySheetPrintViewProps {
  eventId: string;
  onClose?: () => void;
}

export function DaySheetPrintView({ eventId, onClose }: DaySheetPrintViewProps) {
  const {
    data: sheet,
    isLoading,
    error,
  } = useQuery<DaySheet>({
    queryKey: ["events", eventId, "day-sheet"],
    queryFn: () => eventsApi.getEventDaySheet(eventId),
  });

  if (isLoading) {
    return (
      <Panel title="برگه روز مراسم">
        <p className="muted">در حال بارگذاری برگه روز مراسم...</p>
      </Panel>
    );
  }

  if (error || !sheet) {
    return (
      <Panel title="برگه روز مراسم">
        <ErrorLine error={error instanceof Error ? error.message : "خطا در دریافت برگه روز مراسم"} />
        {onClose && (
          <Button variant="ghost" onClick={onClose}>
            بازگشت
          </Button>
        )}
      </Panel>
    );
  }

  const { event, tasks, menu, needs, emergencyContacts } = sheet;

  // Sort tasks chronologically by due time
  const sortedTasks = [...tasks].sort((a, b) => new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime());

  return (
    <div className="day-sheet-container" style={{ direction: "rtl" }}>
      {/* Non-printed action bar */}
      <div
        className="no-print row wrap"
        style={{
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "var(--s4)",
          padding: "var(--s3)",
          background: "var(--card)",
          borderRadius: "var(--r-card)",
          border: "1px solid var(--card-line)",
        }}
      >
        <div className="row" style={{ alignItems: "center", gap: "var(--s2)" }}>
          <Icon name="printer" />
          <span style={{ fontWeight: 600 }}>برگه چاپی روز مراسم (A4)</span>
        </div>
        <div className="row" style={{ gap: "var(--s2)" }}>
          <Button
            variant="primary"
            icon="printer"
            onClick={() => {
              window.print();
            }}
          >
            چاپ برگه (Print)
          </Button>
          {onClose && (
            <Button variant="ghost" onClick={onClose}>
              بستن
            </Button>
          )}
        </div>
      </div>

      {/* Printable Sheet (Standard A4 layout) */}
      <div
        className="day-sheet-paper"
        style={{
          background: "#ffffff",
          color: "#10141a",
          padding: "24px 32px",
          borderRadius: "var(--r-card)",
          border: "1px solid var(--line)",
          boxShadow: "var(--sh-card)",
          maxWidth: "850px",
          margin: "0 auto",
          fontFamily: "YekanBakh, sans-serif",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            borderBottom: "2px solid #10141a",
            paddingBottom: "16px",
            marginBottom: "20px",
          }}
        >
          <div>
            <div style={{ fontSize: "14px", color: "#5c6372", marginBottom: "4px" }}>
              سامانه تشریفات و مراسم آشپزخانه مرکزی
            </div>
            <h1 style={{ fontSize: "22px", margin: "0 0 6px 0", fontWeight: 700 }}>
              برگه روز مراسم: {event.name}
            </h1>
            <div style={{ fontSize: "13px", color: "#3a4050" }}>
              محل برگزاری: <strong>{event.place}</strong> | آشپزخانه مسئول:{" "}
              <strong>{event.kitchenName}</strong>
            </div>
          </div>
          <div style={{ textAlign: "left", direction: "ltr" }}>
            <div style={{ fontSize: "14px", fontWeight: 700 }}>{fmtDate(event.startAt)}</div>
            <div style={{ fontSize: "13px", color: "#5c6372" }}>
              {formatTime(event.startAt)} تا {formatTime(event.endAt)}
            </div>
            <div style={{ fontSize: "13px", marginTop: "4px" }}>
              تعداد مهمان: <strong>{faDigits(event.guestCount)} نفر</strong>
            </div>
          </div>
        </div>

        {/* Section: Emergency Contacts (§13.6 who) */}
        <div style={{ marginBottom: "20px" }}>
          <h2
            style={{
              fontSize: "15px",
              fontWeight: 700,
              margin: "0 0 10px 0",
              background: "#f0f4f8",
              padding: "6px 12px",
              borderRadius: "6px",
            }}
          >
            شماره‌های اضطراری و افراد کلیدی (تماس مستقیم)
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: "10px",
            }}
          >
            {emergencyContacts.map((c, i) => (
              <div
                key={i}
                style={{
                  padding: "8px 12px",
                  border: "1px solid #e0e4ec",
                  borderRadius: "6px",
                  background: "#fafbfc",
                }}
              >
                <div style={{ fontSize: "12px", color: "#5c6372" }}>{c.role}</div>
                <div style={{ fontWeight: 600, fontSize: "14px" }}>{c.name}</div>
                <div style={{ fontSize: "13px", color: "#2563eb", direction: "ltr", textAlign: "right" }}>
                  {faDigits(c.phone)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Section: Hour-by-hour timeline (§13.6 when & what) */}
        <div style={{ marginBottom: "20px" }}>
          <h2
            style={{
              fontSize: "15px",
              fontWeight: 700,
              margin: "0 0 10px 0",
              background: "#f0f4f8",
              padding: "6px 12px",
              borderRadius: "6px",
            }}
          >
            جدول زمان‌بندی و کارهای روز مراسم (ساعت‌به‌ساعت)
          </h2>
          {sortedTasks.length === 0 ? (
            <p className="muted" style={{ fontSize: "13px" }}>
              کاری ثبت نشده است.
            </p>
          ) : (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "13px",
              }}
            >
              <thead>
                <tr style={{ background: "#f8f9fa", borderBottom: "2px solid #d0d7de" }}>
                  <th style={{ padding: "8px", textAlign: "right", width: "100px" }}>زمان موعد</th>
                  <th style={{ padding: "8px", textAlign: "right" }}>عنوان کار و چک‌لیست</th>
                  <th style={{ padding: "8px", textAlign: "right", width: "150px" }}>مسئول انجام</th>
                  <th style={{ padding: "8px", textAlign: "center", width: "90px" }}>وضعیت</th>
                </tr>
              </thead>
              <tbody>
                {sortedTasks.map((t) => (
                  <tr
                    key={t.id}
                    style={{
                      borderBottom: "1px solid #e5e7eb",
                      verticalAlign: "top",
                    }}
                  >
                    <td style={{ padding: "8px", fontWeight: 600, color: "#1f2937" }}>
                      {formatTime(t.dueAt)}
                      <div style={{ fontSize: "11px", color: "#6b7280" }}>{fmtDate(t.dueAt)}</div>
                    </td>
                    <td style={{ padding: "8px" }}>
                      <div style={{ fontWeight: 600, marginBottom: "4px" }}>{t.title}</div>
                      {t.checklist.length > 0 && (
                        <div style={{ paddingRight: "10px", fontSize: "12px", color: "#4b5563" }}>
                          {t.checklist.map((c, ci) => (
                            <div key={ci} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              <span>{c.done ? "☑" : "☐"}</span>
                              <span style={{ textDecoration: c.done ? "line-through" : "none" }}>
                                {c.text}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      {t.note && (
                        <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "2px" }}>
                          یادداشت: {t.note}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "8px" }}>{t.assignedStaffName ?? "تعیین‌نشده"}</td>
                    <td style={{ padding: "8px", textAlign: "center" }}>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "11px",
                          background:
                            t.status === "done"
                              ? "#e6f4ea"
                              : t.status === "in_progress"
                                ? "#e8f0fe"
                                : "#fef7e0",
                          color:
                            t.status === "done"
                              ? "#137333"
                              : t.status === "in_progress"
                                ? "#1a73e8"
                                : "#b06000",
                        }}
                      >
                        {t.status === "done"
                          ? "انجام شد"
                          : t.status === "in_progress"
                            ? "در حال انجام"
                            : "انجام‌نشده"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Section: Menu & Portions (§13.6 menu) */}
        <div style={{ marginBottom: "20px" }}>
          <h2
            style={{
              fontSize: "15px",
              fontWeight: 700,
              margin: "0 0 10px 0",
              background: "#f0f4f8",
              padding: "6px 12px",
              borderRadius: "6px",
            }}
          >
            منوی غذا و پذیرایی مراسم
          </h2>
          {menu.length === 0 ? (
            <p className="muted" style={{ fontSize: "13px" }}>
              ردیف منویی ثبت نشده است.
            </p>
          ) : (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "13px",
              }}
            >
              <thead>
                <tr style={{ background: "#f8f9fa", borderBottom: "2px solid #d0d7de" }}>
                  <th style={{ padding: "6px 8px", textAlign: "right" }}>نام غذا / قلم پذیرایی</th>
                  <th style={{ padding: "6px 8px", textAlign: "center", width: "120px" }}>تعداد پرس</th>
                  <th style={{ padding: "6px 8px", textAlign: "right" }}>قیمت هر پرس</th>
                </tr>
              </thead>
              <tbody>
                {menu.map((m, mi) => (
                  <tr key={mi} style={{ borderBottom: "1px solid #e5e7eb" }}>
                    <td style={{ padding: "6px 8px", fontWeight: 600 }}>{m.foodName}</td>
                    <td style={{ padding: "6px 8px", textAlign: "center", fontWeight: 700 }}>
                      {faDigits(m.portions)} پرس
                    </td>
                    <td style={{ padding: "6px 8px", color: "#5c6372" }}>{formatMoney(m.pricePerPortion)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Section: Needs & Special Requirements (§13.6) */}
        {needs.length > 0 && (
          <div style={{ marginBottom: "20px" }}>
            <h2
              style={{
                fontSize: "15px",
                fontWeight: 700,
                margin: "0 0 10px 0",
                background: "#f0f4f8",
                padding: "6px 12px",
                borderRadius: "6px",
              }}
            >
              ملزومات، خدمات و هماهنگی‌های ویژه
            </h2>
            <ul style={{ margin: "0", paddingRight: "20px", fontSize: "13px", lineHeight: "1.8" }}>
              {needs.map((n, ni) => (
                <li key={ni}>{n}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Footer */}
        <div
          style={{
            borderTop: "1px dashed #d0d7de",
            paddingTop: "12px",
            marginTop: "24px",
            display: "flex",
            justifyContent: "space-between",
            fontSize: "11px",
            color: "#6b7280",
          }}
        >
          <div>
            سامانه جامع آشپزخانه و تشریفات | تولید شده در: {fmtDate(sheet.generatedAt)} -{" "}
            {formatTime(sheet.generatedAt)}
          </div>
          <div>مبلغ بیعانه پرداخت‌شده: {formatMoney(event.depositAmount)}</div>
        </div>
      </div>

      {/* Print CSS styling */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .day-sheet-container,
          .day-sheet-container * {
            visibility: visible;
          }
          .day-sheet-container {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .no-print {
            display: none !important;
          }
          .day-sheet-paper {
            box-shadow: none !important;
            border: none !important;
            padding: 0 !important;
            max-width: 100% !important;
          }
        }
      `}</style>
    </div>
  );
}
